import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { prescriptionApi } from "apis";
import { CustomBreadcrumbs } from "components/common/CustomBreadcrumbs";
import CustomTabs from "components/common/CustomTabs";
import {
  ActivityLog,
  ActivityLogEntry,
} from "components/features/prescription/ActivityLog";
import { FeesAndTotalSummary } from "components/features/prescription/FeesAndTotalSummary";
import { PrescriptionHeader } from "components/features/prescription/PrescriptionHeader";
import {
  PrescriptionItemsTable,
  PrescriptionItem,
} from "components/features/prescription/PrescriptionItemsTable";
import { IPrescriptionOrderInformationCardProps } from "components/features/prescription/PrescriptionOtherInformationCard";
import { IPrescriptionPatientCardProps } from "components/features/prescription/PrescriptionPatientCard";
import { IPrescriptionPrescriberCardProps } from "components/features/prescription/PrescriptionPrescriberCard";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import { PrescriptionResponseDtoStatusEnum } from "libs/cellera-sdk";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

export const PrescriptionDetailPage: React.FC = () => {
  const { prescriptionId } = useParams();
  const { showError } = useToast();
  const {
    data: prescription,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["prescription", prescriptionId],
    queryFn: () =>
      prescriptionApi.adminGetPrescriptionById({
        prescriptionId: prescriptionId as string,
      }),
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      showError("Error", error.message);
    }
  }, [error, showError]);

  // Mock data for medicine items
  const [medicineItems, setMedicineItems] = useState<PrescriptionItem[]>([
    {
      id: "1",
      productName: "Paracetamol 500mg",
      dosage: "1 tablet every 6 hours",
      quantity: 1,
      unit: "tablet",
      price: 1.2,
    },
    {
      id: "2",
      productName: "Amoxicillin 250mg",
      dosage: "1 capsule every 8 hours",
      quantity: 1,
      unit: "capsule",
      price: 1.2,
    },
    {
      id: "3",
      productName: "Ibuprofen 200mg",
      dosage: "1 tablet every 12 hours",
      quantity: 1,
      unit: "tablet",
      price: 1.2,
    },
  ]);

  // Mock data for supplement items
  const [supplementItems, setSupplementItems] = useState<PrescriptionItem[]>([
    {
      id: "4",
      productName: "Vitamin D3 1000IU",
      dosage: "1 capsule daily",
      quantity: 1,
      unit: "capsule",
      price: 0.8,
    },
  ]);

  // Mock data for lab order items
  const [labOrderItems, setLabOrderItems] = useState<PrescriptionItem[]>([
    {
      id: "5",
      productName: "Complete Blood Count",
      dosage: "N/A",
      quantity: 1,
      unit: "test",
      price: 25.0,
    },
  ]);

  const handleDeleteMedicine = (itemId: string) => {
    setMedicineItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleDeleteSupplement = (itemId: string) => {
    setSupplementItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleDeleteLabOrder = (itemId: string) => {
    setLabOrderItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Mock activity log data
  const activityLogEntries: ActivityLogEntry[] = useMemo(() => {
    const entries = [
      {
        timestamp: prescription?.createdAt?.toISOString() ?? "",
        description: `Order created by ${prescription?.provider?.firstName} ${prescription?.provider?.lastName}`,
      },
      {
        timestamp: prescription?.createdAt?.toISOString() ?? "",
        description: `Prescription sent to ${prescription?.patient?.firstName} ${prescription?.patient?.lastName}`,
      },
    ];
    if (prescription?.status === PrescriptionResponseDtoStatusEnum.Paid) {
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Payment completed by ${prescription?.patient?.firstName} ${prescription?.patient?.lastName}`,
      });
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Prescription marked as PAID`,
      });
    }
    if (prescription?.status === PrescriptionResponseDtoStatusEnum.Cancelled) {
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Prescription cancelled by ${prescription?.cancelBy?.firstName} ${prescription?.cancelBy?.lastName}`,
      });
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Prescription marked as CANCELLED`,
      });
    }
    return entries;
  }, [prescription]);

  const handleViewInvoice = () => {
    console.log("View invoice clicked");
  };

  const allActionButtons = useMemo(
    () => [
      {
        icon: <ArrowBackIcon />,
        onClick: () => navigate(EPath.PRESCRIPTION_MANAGEMENT),
        tooltip: "Go back",
        disabled: false,
        sx: {},
      },
      {
        icon: <RefreshIcon />,
        onClick: () => refetch(),
        tooltip: "Refresh",
        disabled: false,
        sx: {},
      },
    ],
    [navigate, refetch],
  );
  const breadcrumbs = useMemo(
    () => [
      {
        label: "Practice Management",
        href: EPath.PRACTICE_MANAGEMENT,
        order: 0,
      },
      {
        label: "Prescription Management",
        href: EPath.PRESCRIPTION_MANAGEMENT,
        order: 1,
      },
    ],
    [],
  );
  const orderInformation = useMemo(
    (): IPrescriptionOrderInformationCardProps => ({
      orderId: prescription?.id?.toString() ?? "",
      status: prescription?.status as
        | PrescriptionResponseDtoStatusEnum
        | undefined,
      createdAt: prescription?.createdAt,
      paidAt:
        prescription?.status === PrescriptionResponseDtoStatusEnum.Paid
          ? prescription?.updatedAt
          : undefined,
      cancelledAt:
        prescription?.status === PrescriptionResponseDtoStatusEnum.Cancelled
          ? prescription?.updatedAt
          : undefined,
    }),
    [
      prescription?.createdAt,
      prescription?.id,
      prescription?.status,
      prescription?.updatedAt,
    ],
  );

  const patientInformation = useMemo(
    (): IPrescriptionPatientCardProps => ({
      name:
        [
          prescription?.patient?.firstName,
          prescription?.patient?.middleName,
          prescription?.patient?.lastName,
        ]
          .filter(Boolean)
          .join(" ") ?? "N/A",
      email: prescription?.patient?.email ?? "N/A",
      phone: prescription?.patient?.phone ?? "N/A",
      patientId: prescription?.patientId,
    }),
    [prescription?.patient, prescription?.patientId],
  );

  const prescriberInformation = useMemo(
    (): IPrescriptionPrescriberCardProps => ({
      name:
        [prescription?.provider?.firstName, prescription?.provider?.lastName]
          .filter(Boolean)
          .join(" ") ?? "N/A",
      email: prescription?.provider?.email ?? "N/A",
      phone: prescription?.provider?.phoneNumber ?? "N/A",
    }),
    [prescription?.provider],
  );
  if (isLoading) {
    return (
      <DashboardLayout title="Prescription Details">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout title="Prescription Details">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CustomBreadcrumbs
          items={breadcrumbs}
          lastItem={"Prescription Details"}
        />
        {allActionButtons.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {allActionButtons.map((button, index) => (
              <IconButton
                key={index}
                onClick={button.onClick}
                disabled={button.disabled ?? false}
                sx={{ color: "text.secondary", ...(button.sx ?? {}) }}
                size="small"
                title={button.tooltip}
              >
                {button.icon}
              </IconButton>
            ))}
          </Box>
        )}
      </Box>

      <PrescriptionHeader
        orderInformation={orderInformation}
        patientInformation={patientInformation}
        prescriberInformation={prescriberInformation}
      />

      <Box sx={{ mt: 3 }}>
        <CustomTabs
          tabs={[
            {
              label: "Medicine",
              content: (
                <PrescriptionItemsTable
                  items={medicineItems}
                  onDelete={handleDeleteMedicine}
                />
              ),
            },
            {
              label: "Supplement",
              content: (
                <PrescriptionItemsTable
                  items={supplementItems}
                  onDelete={handleDeleteSupplement}
                />
              ),
            },
            {
              label: "Lab Order",
              content: (
                <PrescriptionItemsTable
                  items={labOrderItems}
                  onDelete={handleDeleteLabOrder}
                />
              ),
            },
          ]}
          defaultTab={0}
        />
      </Box>

      {prescription?.status === PrescriptionResponseDtoStatusEnum.Paid && (
        <Box>
          <FeesAndTotalSummary
            medicineItems={medicineItems}
            supplementItems={supplementItems}
            labOrderItems={labOrderItems}
            status={prescription?.status as PrescriptionResponseDtoStatusEnum}
            onViewInvoice={handleViewInvoice}
          />
        </Box>
      )}
      <Box sx={{ mt: 3 }}>
        <ActivityLog activities={activityLogEntries} />
      </Box>
    </DashboardLayout>
  );
};

export default PrescriptionDetailPage;
